import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const fmt = (n) => (n ? `+${n.slice(0, 2)} ${n.slice(2)}` : "—");
const METHOD = { forward: "Call forwarding", connected: "Connected number", new: "New number" };
const cell = "px-3 py-2.5";

function useList(path) {
  const [rows, setRows] = useState(null);
  const reload = useCallback(() => { api.get(path).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [path]);
  useEffect(() => { reload(); }, [reload]);
  return [rows, reload];
}

function Table({ head, children, testid }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-xl border border-slate-800" data-testid={testid}>
      <table className="w-full text-sm"><thead className="bg-slate-900/70 text-left text-xs uppercase text-slate-500"><tr>{head.map((h) => <th key={h} className={`${cell} font-medium`}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody></table>
    </div>
  );
}

function AssignForm({ prefill, onDone }) {
  const [tid, setTid] = useState(""), [opts, setOpts] = useState(null), [bid, setBid] = useState(""), [num, setNum] = useState(""), [busy, setBusy] = useState(false);
  const load = async (t, b = "") => {
    setOpts(null); setBid(b);
    if (!t) return;
    try { setOpts((await api.get("/platform/phone-branches", { params: { tenant_id: t } })).data); } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { if (prefill) { setTid(prefill.tenant_id); load(prefill.tenant_id, prefill.branch_id); } }, [prefill]);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/platform/phone-numbers", { number: num, tenant_id: tid, branch_id: bid }); toast.success("Number assigned (not active until the business activates it)"); setNum(""); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const inp = "h-9 rounded-md border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100";
  return (
    <div className="mt-3 grid gap-2 rounded-xl border border-slate-800 p-4 md:grid-cols-4" data-testid="pa-phone-assign">
      <input className={inp} value={tid} onChange={(e) => setTid(e.target.value.trim())} onBlur={() => load(tid)} placeholder="Business ID (ten_…)" data-testid="pa-phone-tenant" />
      <select className={inp} value={bid} onChange={(e) => setBid(e.target.value)} disabled={!opts} data-testid="pa-phone-branch">
        <option value="">{opts ? `Branch of ${opts.business.name}` : "Load a business first"}</option>
        {opts?.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <input className={inp} value={num} onChange={(e) => setNum(e.target.value)} placeholder="Existing Plivo number, +91…" data-testid="pa-phone-number" />
      <button type="button" disabled={busy || !tid || !bid || !num.trim()} onClick={submit} className="h-9 rounded-md bg-emerald-600 px-3 text-sm font-semibold text-white disabled:opacity-40" data-testid="pa-phone-assign-btn">Assign number</button>
      <p className="text-xs text-slate-500 md:col-span-4">Only numbers already rented in Pawkt's Plivo account. Each number can belong to one business branch; nothing is purchased here.</p>
    </div>
  );
}

function SalesLineForm({ onDone }) {
  const [num, setNum] = useState(""), [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!window.confirm(`Register ${num.trim()} as Pawkt's own sales line? It is created switched OFF, GPT-Live only. Nothing is purchased and Plivo routing is not changed.`)) return;
    setBusy(true);
    try { await api.post("/platform/sales-lines", { number: num }); toast.success("Sales line registered (switched off)"); setNum(""); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 grid gap-2 rounded-xl border border-slate-800 p-4 md:grid-cols-4" data-testid="pa-sales-line">
      <input className="h-9 rounded-md border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100 md:col-span-3" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Existing Pawkt Plivo number, +91…" data-testid="pa-sales-line-number" />
      <button type="button" disabled={busy || !num.trim()} onClick={submit} className="h-9 rounded-md bg-emerald-600 px-3 text-sm font-semibold text-white disabled:opacity-40" data-testid="pa-sales-line-btn">{busy ? "Registering…" : "Register sales line"}</button>
      <p className="text-xs text-slate-500 md:col-span-4">For Pawkt's own sales/test number only (no customer business). Created switched OFF; nothing is purchased and call routing is not changed.</p>
    </div>
  );
}

function ShallyCell({ n, onDone }) {
  const live = n.voice_engine === "gpt-live-1";
  const save = async (gpt_live, leads_bookings) => {
    const msg = gpt_live ? `Switch ${fmt(n.number)} to GPT-Live Shally${leads_bookings ? " with leads & bookings" : ""}? Live calls also need the platform calling switch.` : `Return ${fmt(n.number)} to the classic receptionist?`;
    if (!window.confirm(msg)) return;
    try { await api.patch(`/platform/phone-numbers/${n.id}/shally`, { gpt_live, leads_bookings, classic_fallback: false }); toast.success("Saved"); onDone(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-1" data-testid={`pa-num-shally-${n.number}`}>
      <span className={live ? "text-emerald-300" : "text-slate-400"}>{live ? `GPT-Live Shally${n.shally_actions_v1 ? " · leads & bookings" : ""}` : "Classic"}</span>
      <div className="flex gap-2 text-xs">
        {live
          ? <><button type="button" className="underline text-slate-300" onClick={() => save(true, !n.shally_actions_v1)} data-testid={`pa-num-actions-${n.number}`}>{n.shally_actions_v1 ? "Turn off leads & bookings" : "Turn on leads & bookings"}</button>
            <button type="button" className="underline text-amber-300" onClick={() => save(false, false)} data-testid={`pa-num-classic-${n.number}`}>Use classic</button></>
          : <button type="button" className="underline text-emerald-300" onClick={() => save(true, true)} data-testid={`pa-num-gptlive-${n.number}`}>Use GPT-Live Shally</button>}
      </div>
    </div>
  );
}

export default function PlatformPhoneNumbers() {
  const [setups, reloadSetups] = useList("/platform/receptionist-setups");
  const [nums, reloadNums] = useList("/platform/phone-numbers");
  const [prefill, setPrefill] = useState(null), [preparing, setPreparing] = useState("");
  const done = () => { reloadSetups(); reloadNums(); };
  const prepare = async (s) => {
    if (!window.confirm(`Verify ${fmt(s.business_number)} in Plivo and prepare it for ${s.business_name || s.tenant_id} (${s.branch_name || s.branch_id})? It stays switched OFF and call routing is not changed.`)) return;
    setPreparing(s.branch_id);
    try { const r = (await api.post(`/platform/receptionist-setups/${s.tenant_id}/${s.branch_id}/prepare-number`, {})).data; toast.success(r.status === "already_prepared" ? "Already prepared (off)" : `Prepared, switched off · routing: ${r.routing}`); done(); }
    catch (e) { toast.error(apiErr(e)); } finally { setPreparing(""); }
  };
  const unassign = async (n) => {
    if (!window.confirm(`Unassign ${fmt(n.number)} from ${n.business_name}?`)) return;
    try { await api.delete(`/platform/phone-numbers/${n.id}`); toast.success("Unassigned"); done(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="pa-phone-numbers">
      <h1 className="font-display text-2xl font-bold">AI Receptionist Numbers</h1>
      <p className="mt-1 text-sm text-slate-400">Map existing Plivo numbers to a business and one of its branches. Businesses activate the receptionist themselves after readiness checks.</p>
      <h2 className="mt-6 font-semibold">Setup requests</h2>
      <Table head={["Business", "Branch", "Connection", "Business number", "Number", ""]} testid="pa-phone-setups">
        {setups?.map((s) => (
          <tr key={`${s.tenant_id}-${s.branch_id}`} className="border-t border-slate-800" data-testid={`pa-setup-${s.branch_id}`}>
            <td className={cell}>{s.business_name || s.tenant_id}<div className="text-xs text-slate-500">{s.tenant_id}</div></td>
            <td className={cell}>{s.branch_name || s.branch_id}</td>
            <td className={cell}>{METHOD[s.connection_method] || s.connection_method}</td>
            <td className={cell}>{fmt(s.business_number)}</td>
            <td className={cell}>{s.number_mapped ? <span className="text-emerald-300">Assigned</span> : <span className="text-amber-300">Pending</span>}</td>
            <td className={cell}>{!s.number_mapped && <div className="flex gap-3">
              {s.business_number && <button type="button" disabled={preparing === s.branch_id} className="text-xs font-semibold text-emerald-300 underline disabled:opacity-40" onClick={() => prepare(s)} data-testid={`pa-setup-prepare-${s.branch_id}`}>{preparing === s.branch_id ? "Verifying…" : "Verify & prepare"}</button>}
              <button type="button" className="text-xs text-slate-300 underline" onClick={() => setPrefill({ ...s })} data-testid={`pa-setup-pick-${s.branch_id}`}>Assign…</button></div>}</td>
          </tr>))}
        {setups && !setups.length && <tr><td colSpan={6} className={`${cell} text-center text-slate-500`}>No setup requests yet</td></tr>}
      </Table>
      <h2 className="mt-6 font-semibold">Assign a number</h2>
      <AssignForm prefill={prefill} onDone={done} />
      <h2 className="mt-6 font-semibold">Register Pawkt sales line</h2>
      <SalesLineForm onDone={done} />
      <h2 className="mt-6 font-semibold">Assigned numbers</h2>
      <Table head={["Number", "Business", "Branch", "Status", "Receptionist", "Assigned", ""]} testid="pa-phone-list">
        {nums?.map((n) => (
          <tr key={n.id} className="border-t border-slate-800" data-testid={`pa-num-${n.number}`}>
            <td className={`${cell} font-medium`}>{fmt(n.number)}</td>
            <td className={cell}>{n.business_name}</td>
            <td className={cell}>{n.branch_name || n.branch_id}</td>
            <td className={cell}>{n.enabled ? <span className="text-emerald-300">Active</span> : <span className="text-slate-400">Assigned, not active</span>}</td>
            <td className={cell}><ShallyCell n={n} onDone={done} /></td>
            <td className={`${cell} text-slate-500`}>{n.assigned_at ? new Date(n.assigned_at).toLocaleDateString("en-IN") : "—"}</td>
            <td className={cell}><button type="button" className="text-xs text-rose-300 underline" onClick={() => unassign(n)} data-testid={`pa-num-unassign-${n.number}`}>Unassign</button></td>
          </tr>))}
        {nums && !nums.length && <tr><td colSpan={7} className={`${cell} text-center text-slate-500`}>No numbers assigned</td></tr>}
      </Table>
    </div>
  );
}
